/* hs-eslint ignored failing-rules */

/* eslint-disable no-bitwise */
'use es6';

import { TAB_CLOSE, TAB_OPEN, TAB_MOVE, TAB_UPDATE, TAB_REPLACE, TABS_FETCH, TABS_SAVE, TABS_CLEAR, TABS_CLOSE, TAB_REDIRECT_ADD, TABS_REDIRECTS_CLEAR, TOGGLE_TABS_SCROLL_INTO_VIEW } from 'DesignData/actions/ActionTypes';
import { push, replace } from 'DesignData/actions/History';
import tabbingHelper from 'DesignData/helpers/tabbingHelper';
import { createPath } from 'DesignData/helpers/routingHelper';
import Tab from 'DesignData/models/Tab';
import * as client from 'DesignData/api/Tabs';
import { getPortalId, getUserId } from 'DesignData/selectors/auth';
import { getTabs, getTabsById, getTabsOrder, getActiveTabId } from 'DesignData/selectors/tabs';
import { createAsyncActions } from 'DesignData/lib/actions';
import { FOLDER } from 'DesignData/enums/DesignAssetTypes';
const getAsyncActions = createAsyncActions.bind(null, 'tabs');
/**
 * Ensures passed tab object is-a Tab.
 * @throws {TypeError}
 */

function validateTab(tab) {
  if (tab instanceof Tab) {
    return true;
  }

  throw new TypeError(`Instance of DesignData/models/Tab expected, received ${tab}`);
}
/**
 * Fetch persisted tabs - for application startup.
 */


export function fetch() {
  return (dispatch, getState) => {
    const state = getState();
    const portalId = getPortalId(state);
    const userId = getUserId(state);
    const tabs = client.fetch(userId, portalId);
    return dispatch({
      type: TABS_FETCH,
      tabs
    });
  };
}
/**
 * Save tabs.
 */

export function saveTabs() {
  return (dispatch, getState) => {
    const {
      request,
      receive,
      error
    } = saveTabs;
    const state = getState();
    const tabs = getTabs(state);
    const portalId = getPortalId(state);
    const userId = getUserId(state);
    dispatch(request());
    return client.save(userId, portalId, tabbingHelper.getModifiedTabs(tabs)).then(result => dispatch(receive(result))).catch(err => dispatch(error(err)));
  };
}
Object.assign(saveTabs, getAsyncActions(TABS_SAVE));
/**
 * Remove all tabs.
 * Intended for use with selenium tests and query param `?clearTabs`
 */

export function clearTabs() {
  return {
    type: TABS_CLEAR
  };
} // We created this new async function, because the push to the new route (and the subsequent fetches for new tabs)
// need to happen *before* we close the tab.

const pushNewRoute = (tab, redirect) => (dispatch, getState) => Promise.resolve().then(() => {
  const state = getState();
  const activeTabId = getActiveTabId(state);
  const byId = getTabsById(state);
  const order = getTabsOrder(state);

  if (tab.id === activeTabId) {
    if (redirect) {
      // If closing active tab, redirect to nearest tab.
      const nearestTab = tabbingHelper.findNearestTab(byId, order, tab);
      const pathname = nearestTab ? createPath(nearestTab.assetType, nearestTab.id, nearestTab.editorOptions) : createPath();
      dispatch(push({
        pathname
      }));
    }
  }
});
/**
 * Close tab.
 *
 * @param {Object} tab
 * @throws {TypeError}
 */


export function closeTab(tab, redirect = true) {
  validateTab(tab);
  /* eslint-disable no-unused-vars */

  return (dispatch, getState) => {
    /* eslint-disable promise/catch-or-return */
    dispatch(pushNewRoute(tab, redirect)).then(() => dispatch({
      type: TAB_CLOSE,
      tab
    }));
  };
}
/**
 * Close existing Folder tabs
 */

export function closeFolderTabs() {
  return (dispatch, getState) => {
    const state = getState();
    const tabs = getTabsById(state);
    Object.values(tabs || {}).forEach(currTab => {
      if ((currTab || {}).assetType === FOLDER) {
        dispatch(closeTab(currTab, false));
      }
    });
  };
}
/**
 * Open tab.
 *
 * @param {Object}  tab
 * @param {Boolean} redirect  True to dispatch new URL to history, false otherwise. Default is true. False use-case is onEnter hook of router.
 * @throws {TypeError}
 */

export function openTab(tab, redirect = true) {
  return (dispatch, getState) => {
    validateTab(tab);

    if (tab.assetType === FOLDER) {
      //  Close existing folder tabs before opening a new one
      dispatch(closeFolderTabs());
    }

    dispatch({
      type: TAB_OPEN,
      tab,
      tabs: getTabs(getState())
    });

    if (redirect) {
      const pathname = createPath(tab.assetType, tab.id, tab.editorOptions);
      dispatch(push({
        pathname
      }));
    }
  };
}
/**
 * Open tab represented by plain object
 *
 * @param {Object}  tab
 * @param {Boolean} redirect  True to dispatch new URL to history, false otherwise. Default is true. False use-case is onEnter hook of router.
 * @throws {TypeError}
 */

export function openTabObject(tabObject, redirect = true) {
  return dispatch => {
    const tab = new Tab(tabObject);
    dispatch(openTab(tab, redirect));
  };
}

const redirectWithClose = path => dispatch => Promise.resolve().then(() => {
  const pathname = path || createPath();
  dispatch(push({
    pathname
  }));
});
/**
 * Bulk close tabs.
 *
 * @param {Array.<DesignData/models/Tab>} tabs
 * @throws {TypeError}
 */


export function closeTabs(tabs) {
  return (dispatch, getState) => {
    const state = getState();
    const activeTabId = getActiveTabId(state);
    const byId = getTabsById(state);
    const order = getTabsOrder(state);

    if (!tabs || tabs.length === order.length) {
      dispatch(redirectWithClose()).then(() => {
        dispatch(clearTabs());
      });
      return;
    }

    const tabIds = tabs.map(tab => tab.id);
    tabs.forEach(validateTab);
    let activeTabIndex = tabIds.indexOf(activeTabId);

    if (activeTabIndex) {
      let nearestTab;
      let reversed = false;

      while (~activeTabIndex) {
        nearestTab = tabbingHelper.findNearestTab(byId, order, tabs[activeTabIndex], reversed);
        activeTabIndex = tabIds.indexOf(nearestTab && nearestTab.id);
        if (order.indexOf(nearestTab.id) === 0) reversed = true;
      }

      const pathname = nearestTab ? createPath(nearestTab.assetType, nearestTab.id, nearestTab.editorOptions) : createPath();
      dispatch(redirectWithClose(pathname)).then(() => {
        dispatch({
          type: TABS_CLOSE,
          tabs
        });
      });
    }

    return;
  };
}
/**
 * Move tab to new index.
 *
 * @param {String} tabId
 * @param {Integer} newIndex
 */

export function moveTab(tabId, newIndex) {
  return {
    type: TAB_MOVE,
    tabId,
    newIndex
  };
}
/**
 * Update tab with new data
 *
 * @param {Object} tab
 * @param {Object} data
 */

export function updateTab(tab, data) {
  return {
    type: TAB_UPDATE,
    tab,
    data
  };
}
/**
 * Replace an existing tab.
 * @param {Tab} tab
 * @param {Tab} newTab
 */

export function replaceTab(tab, newTab, redirect = true) {
  return dispatch => {
    const pathname = createPath(newTab.assetType, newTab.id, newTab.editorOptions);
    dispatch({
      type: TAB_REPLACE,
      tab,
      newTab
    });
    if (!redirect) return;
    dispatch(replace({
      pathname
    }));
  };
}
/**
 * Toggle scroll into view
 */

export function toggleShouldScrollActiveTabIntoView() {
  return {
    type: TOGGLE_TABS_SCROLL_INTO_VIEW
  };
}
export function refreshTab(tab) {
  return dispatch => {
    dispatch(closeTab(tab));
    dispatch(openTab(tab));
  };
}
export const addTabRedirect = (oldTabId, newTabId) => {
  return dispatch => {
    if (oldTabId === newTabId) return;
    dispatch({
      type: TAB_REDIRECT_ADD,
      from: oldTabId,
      to: newTabId
    });
    dispatch(saveTabs());
  };
};
/**
 * This is used to remove temporarily redirected tabs when they become inactive,
 * such as focusing another tab. It allows us to minimize the time that the
 * tab state contains redirects, which requires heavier logic.
 */

export const replaceNonActiveTabRedirects = (tabs, redirects, activeTabId) => {
  return dispatch => {
    dispatch({
      type: TABS_REDIRECTS_CLEAR
    });
    Object.keys(redirects).forEach(redirectId => {
      if (redirectId === activeTabId) return;
      const tabToReplace = tabs.byId[redirectId];

      if (tabToReplace) {
        dispatch(replaceTab(tabToReplace, new Tab(Object.assign({}, tabToReplace, {
          id: redirects[redirectId]
        })), false));
      }
    });
  };
};